/*
 * A video project, as it is kept inside the design JSON under the key `video` (next to `objects`, like `print`).
 *
 *   { "version": "5.3.0", "objects": [...], "extraPages": [], "video": { "v": 2, "clips": [...], "music": null, "fadeIn": false, "fadeOut": false } }
 *
 * v2 (วิดีโอ V2) adds the fades: `fadeIn`/`fadeOut` (from/to black at the start and end of the video) and each clip's
 * `fade` (dip to black at the joint before it). A v1 project is read as v2 with no fades (upgradeVideo); the app always
 * writes v2, so an older app open somewhere refuses it ("รุ่นที่ระบบไม่รู้จัก") instead of saving over the fades.
 *
 * v3 (เทมเพลตวิดีโอ) adds to each clip: `kind` ('video' or 'photo' — a still picture that zooms in slowly), `hold`
 * (seconds the last picture stays when a clip is shorter than its slot) and `slot` (a template's place for a clip:
 * label + hint). A slot not filled yet has `media: ''`: it keeps its length so the text over the video stays on time.
 *
 * v4 (เสียงพากย์ AI, 0016) adds the narration: each clip may have `voice` (the words to speak in that scene, the AI voice
 * file made from them, its length) and the project has `narration` (the voice used by default and whether subtitles
 * are made from the words). A scene grows to fit its narration (fitVoices: the last picture is held for the rest).
 * The voice files are on the server (R2, /api/voice/:id/file), so the narration plays on every device.
 *
 * Text, logos and pictures over the video are the design's own objects (`objects`, page 1), edited on the design
 * screen. Each one carries when it shows: `vStart`/`vEnd` (seconds of the finished video) and `vFade` (fades in and
 * out). checkOverlays() is the one check of those, used when editing, by the server and when opening.
 *
 * The clips themselves are NOT here: each clip is a 1080p copy of part of a file from the phone, kept only on the
 * device that imported it (src/video/media.ts, under `media`). The JSON keeps where each copy came from so the
 * same part can be cut again from the original file on another device ("เลือกไฟล์เดิม").
 *
 * Pure: no DOM or Workers types. The server (worker/rules.ts) uses checkVideo() too, so both sides agree on the limits.
 */

/** longest finished video (seconds) */
export const VIDEO_MAX_SECONDS = 180;
export const VIDEO_MAX_CLIPS = 60;
/** shortest piece of a clip that can be kept (seconds) */
export const CLIP_MIN_SECONDS = 0.2;
/** the two frames a project can be: portrait for Reels/TikTok, square for posts */
export const FRAMES = { '9:16': { w: 1080, h: 1920 }, '1:1': { w: 1080, h: 1080 } } as const;
export type Frame = keyof typeof FRAMES;

export interface VideoClip {
  /** this piece of the timeline (a split makes two pieces with the same `media`) */
  id: string;
  /** key of the 1080p copy in this device's media store */
  media: string;
  /** the original file's name and length, to recognise it again */
  name: string;
  srcDur: number;
  /** which part of the original the copy holds (seconds of the original) */
  srcStart: number;
  srcEnd: number;
  /** length of the copy (seconds) */
  copyDur: number;
  /** the part of the copy that is used (seconds of the copy) */
  in: number;
  out: number;
  /** 0..1 */
  volume: number;
  hasAudio: boolean;
  /** v2: dip to black at the joint before this clip (ignored on the first clip: that is the project's fadeIn) */
  fade: boolean;
  /** v3: a video copy, or a still picture (zooms in slowly) */
  kind: ClipKind;
  /** v3: seconds the last picture stays after `out` (a clip shorter than its slot) */
  hold: number;
  /** v3: a template's place for a clip; `media: ''` while it is empty */
  slot: ClipSlot | null;
  /** v4: what is spoken in this scene (absent/null = nothing) */
  voice?: ClipVoice | null;
}

/* ---------- เสียงพากย์ AI (v4) ---------- */

export type VoiceProvider = 'gemini' | 'eleven';
export const PROVIDER_NAME: Record<VoiceProvider, string> = { gemini: 'Gemini', eleven: 'ElevenLabs' };
/** a voice: which service, the voice's id there, a name to show, and (Gemini only) how to speak */
export interface VoiceChoice { provider: VoiceProvider; voice: string; label: string; style: string }
/**
 * one scene's narration: `audio` = the voice file's id on the server (null until made), `dur` its length (seconds),
 * `made` = what it was made from (voiceSig), so changed words or voice show as "ต้องสร้างใหม่"; `extra` = seconds the
 * scene was lengthened for it (fitVoices), kept so a shorter voice gives them back
 */
export interface ClipVoice { text: string; choice: VoiceChoice | null; audio: string | null; dur: number; made: string; extra: number }
/** the project's voice (7ก: one for the whole video, a scene may use another) and subtitles (6ก) */
export interface Narration { choice: VoiceChoice | null; subs: boolean }

export const VOICE_TEXT_MAX = 1000;
export const VOICE_STYLE_MAX = 200;
/** the voice starts this long into its scene, and the scene lasts at least this long after it */
export const VOICE_LEAD = 0.2;
export const VOICE_TAIL = 0.4;

/** the voice a clip speaks with: its own, else the project's */
export const voiceOf = (p: VideoProject, c: VideoClip): VoiceChoice | null => c.voice?.choice ?? p.narration.choice;
/** what a voice file is made from: if this changes, the file no longer matches the words */
export const voiceSig = (choice: VoiceChoice | null, text: string) =>
  choice ? `${choice.provider}|${choice.voice}|${choice.provider === 'gemini' ? choice.style.trim() : ''}|${text.trim()}` : '';
/** one scene's narration: none · words but no voice yet · voice made from other words/voice · ready */
export type VoiceState = 'none' | 'missing' | 'stale' | 'ready';
export function voiceState(p: VideoProject, c: VideoClip): VoiceState {
  const v = c.voice;
  if (!v || !v.text.trim()) return 'none';
  if (!v.audio) return 'missing';
  return v.made === voiceSig(voiceOf(p, c), v.text) ? 'ready' : 'stale';
}

/**
 * 1ก: each scene lasts at least as long as its narration (VOICE_LEAD + voice + VOICE_TAIL): the last picture is held
 * for the rest. Only the seconds added for the voice change (`extra`); a hold that was there before stays.
 */
export function fitVoices(p: VideoProject): VideoProject {
  const r = (x: number) => Math.round(x * 1000) / 1000;
  let changed = false;
  const clips = p.clips.map(c => {
    const v = c.voice;
    const extra = v?.extra ?? 0;
    const base = Math.max(0, c.hold - extra);
    const need = v && v.audio && v.dur > 0 ? r(Math.max(0, VOICE_LEAD + v.dur + VOICE_TAIL - (c.out - c.in) - base)) : 0;
    if (Math.abs(need - extra) < 0.0005) return c;
    changed = true;
    return { ...c, hold: r(base + need), voice: v ? { ...v, extra: need } : v };
  });
  return changed ? { ...p, clips } : p;
}

/** where each voice plays on the finished video (seconds) */
export interface VoiceSpan { index: number; audio: string; start: number; end: number }
export function voiceSpans(p: VideoProject): VoiceSpan[] {
  const starts = clipStarts(p), out: VoiceSpan[] = [];
  p.clips.forEach((c, k) => {
    const v = c.voice;
    if (!v?.audio || !(v.dur > 0) || !v.text.trim()) return;
    const start = starts[k] + VOICE_LEAD;
    out.push({ index: k, audio: v.audio, start, end: Math.min(start + v.dur, starts[k] + clipLength(c)) });
  });
  return out;
}

export type ClipKind = 'video' | 'photo';
export interface ClipSlot { label: string; hint: string }
export const SLOT_LABEL_MAX = 40, SLOT_HINT_MAX = 120;
/** a still picture shows at most this long */
export const PHOTO_MAX_SECONDS = 30;
/** a still picture zooms in from 100% to this over its time */
export const PHOTO_ZOOM = 1.1;
/** default length of a new empty slot (seconds) */
export const SLOT_DEFAULT_SECONDS = 3;

/**
 * `library` (0009): the song's id in the group's music library — any device (or a template) can fetch the same
 * file again; null/absent for music chosen from this device only.
 */
export interface VideoMusic { media: string; name: string; dur: number; volume: number; library?: string | null }

const LIBRARY_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

export interface VideoProject {
  v: 4; clips: VideoClip[]; music: VideoMusic | null;
  /** from black at the start / to black at the end of the video (pictures, text and sound) */
  fadeIn: boolean; fadeOut: boolean;
  /** v4: the default voice and subtitles */
  narration: Narration;
}

export type Checked<T> = { ok: true; value: T } | { ok: false; error: string };

export const DEFAULT_NARRATION: Narration = { choice: null, subs: true };
export const emptyProject = (): VideoProject => ({ v: 4, clips: [], music: null, fadeIn: false, fadeOut: false, narration: { ...DEFAULT_NARRATION } });

/**
 * a checked project as v4 (v1: no fades; v1/v2: plain video clips; v1–v3: no narration); the stored JSON becomes v4
 * at the next save — an app from before v4 then refuses it ("รุ่นที่ระบบไม่รู้จัก") instead of saving the narration away
 */
export function upgradeVideo(raw: unknown): VideoProject {
  const p = raw as { v: number; clips: Array<Partial<VideoClip>>; music: VideoMusic | null; fadeIn?: boolean; fadeOut?: boolean; narration?: Narration };
  if (p.v === 4) return p as unknown as VideoProject;
  if (p.v === 3) return { ...(p as unknown as Omit<VideoProject, 'v' | 'narration'>), v: 4, narration: { ...DEFAULT_NARRATION } };
  return {
    v: 4, music: p.music, fadeIn: p.fadeIn === true, fadeOut: p.fadeOut === true, narration: { ...DEFAULT_NARRATION },
    clips: p.clips.map(c => ({ ...c, fade: c.fade === true, kind: 'video', hold: 0, slot: null }) as VideoClip)
  };
}

/** a template's place that has no clip yet */
export const isEmptySlot = (c: VideoClip) => c.media === '';

/** an empty slot `len` seconds long */
export function emptySlot(id: string, len: number, slot: ClipSlot, fade = false): VideoClip {
  const l = Math.round(len * 1000) / 1000;
  return {
    id, media: '', name: '', srcDur: l, srcStart: 0, srcEnd: l, copyDur: l, in: 0, out: l, volume: 1, hasAudio: false,
    fade, kind: 'video', hold: 0, slot: { ...slot }
  };
}

/** how long a clip plays (seconds), including the last picture held to fill its slot */
export const clipLength = (c: VideoClip) => c.out - c.in + (c.hold ?? 0);
/** length of the finished video (seconds) */
export const totalLength = (p: VideoProject) => p.clips.reduce((s, c) => s + clipLength(c), 0);

/** where each clip starts on the timeline (seconds) */
export function clipStarts(p: VideoProject): number[] {
  const starts: number[] = [];
  let t = 0;
  for (const c of p.clips) { starts.push(t); t += clipLength(c); }
  return starts;
}

/** the clip playing at timeline time `t` and the time inside its copy (the last clip at the very end) */
export function clipAt(p: VideoProject, t: number): { index: number; local: number } | null {
  if (!p.clips.length) return null;
  let start = 0;
  for (let k = 0; k < p.clips.length; k++) {
    const len = clipLength(p.clips[k]);
    if (t < start + len || k === p.clips.length - 1) {
      const c = p.clips[k];
      return { index: k, local: Math.min(c.out, Math.max(c.in, c.in + (t - start))) };
    }
    start += len;
  }
  return null;
}

/** "0:38.0" */
export function fmtTime(sec: number): string {
  const s = Math.max(0, sec);
  const m = Math.floor(s / 60);
  const rest = s - m * 60;
  return `${m}:${rest.toFixed(1).padStart(4, '0')}`;
}

/* ---------- edits (each returns a new project, or a Thai reason) ---------- */

export type Edit = { ok: true; project: VideoProject } | { ok: false; error: string };
const done = (project: VideoProject): Edit => ({ ok: true, project });
const no = (error: string): Edit => ({ ok: false, error });
const copy = (p: VideoProject): VideoProject => ({
  ...p, clips: p.clips.map(c => ({ ...c, voice: c.voice && { ...c.voice, choice: c.voice.choice && { ...c.voice.choice } } })),
  music: p.music && { ...p.music }, narration: { ...p.narration, choice: p.narration.choice && { ...p.narration.choice } }
});

/** seconds still free before the limit */
export const timeLeft = (p: VideoProject) => Math.max(0, VIDEO_MAX_SECONDS - totalLength(p));

export function addClip(p: VideoProject, c: VideoClip, at = p.clips.length): Edit {
  if (p.clips.length >= VIDEO_MAX_CLIPS) return no(`วิดีโอหนึ่งมีได้สูงสุด ${VIDEO_MAX_CLIPS} คลิป`);
  if (clipLength(c) < CLIP_MIN_SECONDS - 0.001) return no(`คลิปที่เตรียมได้ยาวแค่ ${clipLength(c).toFixed(2)} วิ สั้นกว่า ${CLIP_MIN_SECONDS} วิ เลือกช่วงให้ยาวขึ้นแล้วนำเข้าใหม่`);
  if (clipLength(c) > timeLeft(p) + 0.05) return no(`วิดีโอยาวได้สูงสุด ${VIDEO_MAX_SECONDS / 60} นาที เหลือที่ว่างอีก ${fmtTime(timeLeft(p))}`);
  const n = copy(p);
  n.clips.splice(at, 0, { ...c });
  return done(n);
}

/** cut clip `index` in two at `local` seconds of its copy */
export function splitClip(p: VideoProject, index: number, local: number, newId: string): Edit {
  const c = p.clips[index];
  if (!c) return no('ไม่พบคลิปที่เลือก');
  if (isEmptySlot(c)) return no('ช่องนี้ยังว่าง เติมคลิปหรือรูปก่อนจึงจะแบ่งได้');
  if (c.kind === 'photo') return no('รูปนิ่งแบ่งไม่ได้ (ความยาวของรูปเท่ากับช่องที่ใส่)');
  if (p.clips.length >= VIDEO_MAX_CLIPS) return no(`วิดีโอหนึ่งมีได้สูงสุด ${VIDEO_MAX_CLIPS} คลิป`);
  if (local - c.in < CLIP_MIN_SECONDS || c.out - local < CLIP_MIN_SECONDS) {
    return no('ตำแหน่งแบ่งชิดหัวหรือท้ายคลิปเกินไป เลื่อนเส้นเวลาเข้ามาอีกนิด');
  }
  const n = copy(p);
  // no fade inside a split; the held last picture stays at the end (second piece); the first piece keeps the slot
  // the narration stays with the first piece (it starts there); the second has none
  n.clips.splice(index, 1, { ...c, out: local, hold: 0, voice: c.voice && { ...c.voice, extra: 0 } }, { ...c, id: newId, in: local, fade: false, slot: null, voice: null });
  return done(n);
}

/** keep only [inPt, outPt] of clip `index` (seconds of its copy) */
export function trimClip(p: VideoProject, index: number, inPt: number, outPt: number): Edit {
  const c = p.clips[index];
  if (!c) return no('ไม่พบคลิปที่เลือก');
  if (isEmptySlot(c)) return no('ช่องนี้ยังว่าง เติมคลิปหรือรูปก่อน');
  if (c.kind === 'photo') return no('รูปนิ่งตัดหัวท้ายไม่ได้ ความยาวของรูปเท่ากับช่องที่ใส่');
  const a = Math.max(0, Math.min(inPt, c.copyDur)), b = Math.max(0, Math.min(outPt, c.copyDur));
  if (b - a < CLIP_MIN_SECONDS) return no(`คลิปต้องยาวอย่างน้อย ${CLIP_MIN_SECONDS} วินาที`);
  const others = totalLength(p) - clipLength(c);
  if (others + (b - a) + c.hold > VIDEO_MAX_SECONDS + 0.05) return no(`วิดีโอยาวได้สูงสุด ${VIDEO_MAX_SECONDS / 60} นาที`);
  const n = copy(p);
  n.clips[index] = { ...c, in: a, out: b };
  return done(n);
}

export function moveClip(p: VideoProject, index: number, dir: -1 | 1): Edit {
  const to = index + dir;
  if (!p.clips[index]) return no('ไม่พบคลิปที่เลือก');
  if (to < 0 || to >= p.clips.length) return no(dir < 0 ? 'คลิปนี้อยู่แรกสุดแล้ว' : 'คลิปนี้อยู่ท้ายสุดแล้ว');
  const n = copy(p);
  [n.clips[index], n.clips[to]] = [n.clips[to], n.clips[index]];
  // a dip belongs to the joint, not to the clip: the joints keep their fades when clips change places
  [n.clips[index].fade, n.clips[to].fade] = [p.clips[index].fade, p.clips[to].fade];
  return done(n);
}

export function removeClip(p: VideoProject, index: number): Edit {
  if (!p.clips[index]) return no('ไม่พบคลิปที่เลือก');
  const n = copy(p);
  n.clips.splice(index, 1);
  return done(n);
}

export function setClipVolume(p: VideoProject, index: number, volume: number): Edit {
  if (!p.clips[index]) return no('ไม่พบคลิปที่เลือก');
  const n = copy(p);
  n.clips[index].volume = Math.max(0, Math.min(1, volume));
  return done(n);
}

export function setMusic(p: VideoProject, music: VideoMusic | null): Edit {
  const n = copy(p);
  n.music = music && { ...music, volume: Math.max(0, Math.min(1, music.volume)) };
  return done(n);
}

/** fades at the start and end of the video */
export function setEndFades(p: VideoProject, fades: { fadeIn?: boolean; fadeOut?: boolean }): Edit {
  const n = copy(p);
  if (fades.fadeIn !== undefined) n.fadeIn = fades.fadeIn;
  if (fades.fadeOut !== undefined) n.fadeOut = fades.fadeOut;
  return done(n);
}

/** dip to black at the joint before clip `index` (index ≥ 1), or at every joint when `index` is 'all' */
export function setJointFade(p: VideoProject, index: number | 'all', on: boolean): Edit {
  if (index === 'all') {
    if (p.clips.length < 2) return no('ต้องมีอย่างน้อย 2 คลิปจึงจะมีรอยต่อ');
    const n = copy(p);
    n.clips.forEach((c, k) => { if (k > 0) c.fade = on; });
    return done(n);
  }
  if (!p.clips[index]) return no('ไม่พบคลิปที่เลือก');
  if (index === 0) return no('คลิปแรกไม่มีรอยต่อก่อนหน้า ใช้ "จางเข้าตอนเริ่มวิดีโอ" แทน');
  const n = copy(p);
  n.clips[index].fade = on;
  return done(n);
}

/** the words spoken in clip `index` (the voice file stays until it is made again; it shows as "ต้องสร้างใหม่") */
export function setVoiceText(p: VideoProject, index: number, text: string): Edit {
  const c = p.clips[index];
  if (!c) return no('ไม่พบฉากที่เลือก (อาจถูกลบหรือเลิกทำไป)');
  const t = text.replace(/\r\n?/g, '\n');
  if ([...t].length > VOICE_TEXT_MAX) return no(`ข้อความพากย์ของฉากหนึ่งยาวได้ไม่เกิน ${VOICE_TEXT_MAX} ตัวอักษร (ตอนนี้ ${[...t].length})`);
  const n = copy(p);
  const v = n.clips[index].voice;
  n.clips[index].voice = t.trim() || v?.audio ? { text: t, choice: v?.choice ?? null, audio: v?.audio ?? null, dur: v?.dur ?? 0, made: v?.made ?? '', extra: v?.extra ?? 0 } : null;
  return done(fitVoices(n));
}

/** a voice file came back for clip `index` (made from `sig`, `dur` seconds): the scene grows to fit it */
export function setVoiceAudio(p: VideoProject, index: number, audio: string | null, dur: number, sig: string): Edit {
  const c = p.clips[index];
  if (!c?.voice) return no('ไม่พบฉากที่สร้างเสียงนี้ (อาจถูกลบหรือเลิกทำไประหว่างรอ) เสียงที่สร้างแล้วไม่ได้ใส่');
  if (audio && !(dur > 0 && dur <= VIDEO_MAX_SECONDS)) return no('ความยาวของเสียงพากย์ที่ได้ไม่ถูกต้อง');
  const n = copy(p);
  n.clips[index].voice = { ...c.voice, audio, dur: audio ? Math.round(dur * 1000) / 1000 : 0, made: audio ? sig : '' };
  const fitted = fitVoices(n);
  if (totalLength(fitted) > VIDEO_MAX_SECONDS + 0.05) {
    return no(`เสียงพากย์ฉากที่ ${index + 1} ยาว ${dur.toFixed(1)} วิ ฉากต้องยืดตาม แล้ววิดีโอจะยาวเกิน ${VIDEO_MAX_SECONDS / 60} นาที ลดข้อความหรือตัดฉากอื่นให้สั้นลงก่อน`);
  }
  return done(fitted);
}

/** a scene's own voice (null = the project's) */
export function setClipVoiceChoice(p: VideoProject, index: number, choice: VoiceChoice | null): Edit {
  const c = p.clips[index];
  if (!c) return no('ไม่พบฉากที่เลือก');
  if (choice) { const why = checkChoice(choice); if (why) return no(why); }
  const n = copy(p);
  const v = n.clips[index].voice;
  n.clips[index].voice = { text: v?.text ?? '', choice: choice && { ...choice }, audio: v?.audio ?? null, dur: v?.dur ?? 0, made: v?.made ?? '', extra: v?.extra ?? 0 };
  return done(n);
}

/** the project's voice and subtitles */
export function setNarration(p: VideoProject, change: Partial<Narration>): Edit {
  if (change.choice) { const why = checkChoice(change.choice); if (why) return no(why); }
  const n = copy(p);
  n.narration = { ...n.narration, ...change, choice: change.choice !== undefined ? change.choice && { ...change.choice } : n.narration.choice };
  return done(n);
}

/* ---------- slots (เทมเพลตวิดีโอ) ---------- */

const cleanText = (s: string, max: number) => s.replace(/\s+/g, ' ').trim().slice(0, max * 2);

/** a label and hint a person typed, or a Thai reason */
export function checkSlotText(label: string, hint: string): string | null {
  const l = label.replace(/\s+/g, ' ').trim(), h = hint.replace(/\s+/g, ' ').trim();
  if (!l) return 'ต้องมีชื่อช่อง เช่น "หน้าร้าน"';
  if ([...l].length > SLOT_LABEL_MAX) return `ชื่อช่องยาวเกิน ${SLOT_LABEL_MAX} ตัวอักษร`;
  if ([...h].length > SLOT_HINT_MAX) return `คำแนะนำยาวเกิน ${SLOT_HINT_MAX} ตัวอักษร`;
  return null;
}

/** add an empty slot at `at` (end by default) */
export function addSlot(p: VideoProject, id: string, len: number, slot: ClipSlot, at = p.clips.length): Edit {
  const why = checkSlotText(slot.label, slot.hint);
  if (why) return no(why);
  if (!(len >= CLIP_MIN_SECONDS)) return no(`ช่องต้องยาวอย่างน้อย ${CLIP_MIN_SECONDS} วินาที`);
  if (p.clips.length >= VIDEO_MAX_CLIPS) return no(`วิดีโอหนึ่งมีได้สูงสุด ${VIDEO_MAX_CLIPS} คลิป`);
  if (len > timeLeft(p) + 0.05) return no(`วิดีโอยาวได้สูงสุด ${VIDEO_MAX_SECONDS / 60} นาที เหลือที่ว่างอีก ${fmtTime(timeLeft(p))}`);
  const n = copy(p);
  n.clips.splice(at, 0, emptySlot(id, len, { label: cleanText(slot.label, SLOT_LABEL_MAX), hint: cleanText(slot.hint, SLOT_HINT_MAX) }));
  return done(n);
}

/** name a clip's place (any clip: a template made from it keeps the names) */
export function setSlotInfo(p: VideoProject, index: number, slot: ClipSlot): Edit {
  const c = p.clips[index];
  if (!c) return no('ไม่พบคลิปที่เลือก');
  const why = checkSlotText(slot.label, slot.hint);
  if (why) return no(why);
  const n = copy(p);
  n.clips[index].slot = { label: cleanText(slot.label, SLOT_LABEL_MAX), hint: cleanText(slot.hint, SLOT_HINT_MAX) };
  return done(n);
}

/**
 * the part of a copy that fills a slot `len` seconds long: the start of the copy, and the last picture held
 * for the rest when the copy is shorter (choice: ค้างภาพสุดท้าย)
 */
export function fitToSlot(len: number, copyDur: number): { out: number; hold: number } {
  const r = (x: number) => Math.round(x * 1000) / 1000;
  const out = r(Math.min(copyDur, len));
  return { out, hold: r(Math.max(0, len - out)) };
}

/** put a prepared clip or picture into slot `index`; it takes the slot's length, name and fade */
export function fillSlot(p: VideoProject, index: number, filled: VideoClip): Edit {
  const c = p.clips[index];
  if (!c) return no('ไม่พบช่องที่เลือก (อาจถูกลบหรือเลิกทำไป)');
  const len = clipLength(c);
  const n = copy(p);
  if (filled.kind === 'photo') {
    n.clips[index] = { ...filled, id: c.id, in: 0, out: Math.min(len, PHOTO_MAX_SECONDS), copyDur: PHOTO_MAX_SECONDS, hold: Math.max(0, len - PHOTO_MAX_SECONDS), fade: c.fade, slot: c.slot ?? filled.slot, voice: keptVoice(c) };
  } else {
    const fit = fitToSlot(len, filled.copyDur - filled.in);
    n.clips[index] = { ...filled, id: c.id, out: filled.in + fit.out, hold: fit.hold, fade: c.fade, slot: c.slot ?? filled.slot, voice: keptVoice(c) };
  }
  if (clipLength(n.clips[index]) < CLIP_MIN_SECONDS - 0.001) return no(`คลิปที่ได้ยาวไม่ถึง ${CLIP_MIN_SECONDS} วินาที`);
  return done(n);
}

/** empty slot `index` again (its length, name and fade stay) */
export function clearSlot(p: VideoProject, index: number): Edit {
  const c = p.clips[index];
  if (!c) return no('ไม่พบช่องที่เลือก');
  if (isEmptySlot(c)) return no('ช่องนี้ว่างอยู่แล้ว');
  const n = copy(p);
  n.clips[index] = { ...emptySlot(c.id, clipLength(c), c.slot ?? { label: `ฉากที่ ${index + 1}`, hint: '' }, c.fade), voice: keptVoice(c) };
  return done(n);
}

/** a slot keeps its narration when it is filled or emptied (the slot's length already includes the voice's time) */
const keptVoice = (c: VideoClip): ClipVoice | null => c.voice ? { ...c.voice, extra: 0 } : null;

/**
 * a work turned into a template: every clip becomes an empty slot of the same length (clips stay on the admin's
 * device); music from the group's library stays (anyone can fetch it), music from the admin's device does not
 */
export function toTemplate(p: VideoProject): VideoProject {
  return {
    ...p, music: p.music?.library ? { ...p.music } : null,
    // the narration (words and voice file) stays: whoever uses the template gets it with the scene
    clips: p.clips.map((c, k) => ({ ...emptySlot(c.id, clipLength(c), c.slot ?? { label: `ฉากที่ ${k + 1}`, hint: '' }, c.fade), voice: keptVoice(c) }))
  };
}

/** how many slots are still empty */
export const emptySlots = (p: VideoProject) => p.clips.filter(isEmptySlot).length;

/* ---------- text, logos and pictures over the video (the design's objects) ---------- */

/** shortest time one can show (seconds) */
export const OVERLAY_MIN_SECONDS = 0.2;
/** a new one shows this long from the playhead */
export const OVERLAY_NEW_SECONDS = 3;

/** when an object shows: `vStart`/`vEnd` seconds of the finished video, `vFade` fades in and out */
export interface OverlayTiming { vStart: number; vEnd: number; vFade: boolean }

/** an object without timing (none is made so, but older data might be) shows for the whole video */
export function timingOf(o: Partial<OverlayTiming>, total: number): OverlayTiming {
  return typeof o.vStart === 'number' && typeof o.vEnd === 'number'
    ? { vStart: o.vStart, vEnd: o.vEnd, vFade: o.vFade === true }
    : { vStart: 0, vEnd: Math.max(total, OVERLAY_MIN_SECONDS), vFade: false };
}

/** timing for an object added with the playhead at `at` (3 s, moved back when it would pass the end of the video) */
export function newTiming(at: number, total: number): OverlayTiming {
  const end = Math.max(total, OVERLAY_MIN_SECONDS);
  let start = Math.max(0, Math.min(at, VIDEO_MAX_SECONDS - OVERLAY_NEW_SECONDS));
  if (total > 0 && start > end - OVERLAY_NEW_SECONDS) start = Math.max(0, end - OVERLAY_NEW_SECONDS);
  const r = (x: number) => Math.round(x * 1000) / 1000;
  return { vStart: r(start), vEnd: r(Math.min(VIDEO_MAX_SECONDS, total > 0 ? Math.min(end, start + OVERLAY_NEW_SECONDS) : start + OVERLAY_NEW_SECONDS)), vFade: true };
}

/** the timing of one object, or a Thai reason it is not usable */
export function checkTiming(t: { vStart?: unknown; vEnd?: unknown; vFade?: unknown }): string | null {
  const has = (k: 'vStart' | 'vEnd' | 'vFade') => t[k] !== undefined && t[k] !== null;
  if (!has('vStart') && !has('vEnd') && !has('vFade')) return null;
  if (!num(t.vStart) || !num(t.vEnd)) return 'เวลาขึ้น/หายไม่ใช่ตัวเลข';
  const a = t.vStart as number, b = t.vEnd as number;
  if (a < 0) return 'เวลาขึ้นต้องไม่ติดลบ';
  if (b > VIDEO_MAX_SECONDS + 0.05) return `เวลาหายต้องไม่เกิน ${fmtTime(VIDEO_MAX_SECONDS)}`;
  if (b - a < OVERLAY_MIN_SECONDS - 0.001) return `ต้องขึ้นอย่างน้อย ${OVERLAY_MIN_SECONDS} วินาที (จุดเริ่มต้องอยู่ก่อนจุดจบ)`;
  if (has('vFade') && typeof t.vFade !== 'boolean') return 'ค่าจางเข้า-ออกไม่ถูกต้อง';
  return null;
}

/** every object's timing on the video's page; a Thai reason naming the object when one is not usable */
export function checkOverlays(objects: unknown): string | null {
  if (!Array.isArray(objects)) return 'เนื้อหางานไม่มีรายการวัตถุ';
  for (let k = 0; k < objects.length; k++) {
    const o = objects[k];
    if (!o || typeof o !== 'object') continue;
    const why = checkTiming(o as Record<string, unknown>);
    if (why) return `ข้อความ/รูปชิ้นที่ ${k + 1} บนวิดีโอ: ${why}`;
  }
  return null;
}

/* ---------- checking a project (app and server) ---------- */

const MEDIA_ID = /^[A-Za-z0-9_-]{6,64}$/;
const VOICE_FILE_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
const VOICE_NAME = /^[A-Za-z0-9_-]{1,64}$/;
const num = (v: unknown) => typeof v === 'number' && Number.isFinite(v);

/** a voice choice, or a Thai reason it is not usable */
export function checkChoice(c: unknown): string | null {
  if (!c || typeof c !== 'object') return 'ข้อมูลเสียงพากย์เสีย';
  const x = c as Record<string, unknown>;
  if (x.provider !== 'gemini' && x.provider !== 'eleven') return 'บริการเสียงพากย์ต้องเป็น Gemini หรือ ElevenLabs';
  if (typeof x.voice !== 'string' || !VOICE_NAME.test(x.voice)) return 'รหัสเสียงพากย์ไม่ถูกต้อง';
  if (typeof x.label !== 'string' || [...x.label].length > 80) return 'ชื่อเสียงพากย์ยาวเกินไป';
  if (typeof x.style !== 'string' || [...x.style].length > VOICE_STYLE_MAX) return `วิธีพูดยาวได้ไม่เกิน ${VOICE_STYLE_MAX} ตัวอักษร`;
  return null;
}

function checkVoice(v: unknown, where: string): string | null {
  if (v === undefined || v === null) return null;
  if (typeof v !== 'object') return `${where} ข้อมูลเสียงพากย์เสีย`;
  const x = v as Record<string, unknown>;
  if (typeof x.text !== 'string' || [...x.text].length > VOICE_TEXT_MAX) return `${where} ข้อความพากย์ยาวเกิน ${VOICE_TEXT_MAX} ตัวอักษร`;
  if (x.choice !== null) { const why = checkChoice(x.choice); if (why) return `${where}: ${why}`; }
  if (x.audio !== null && (typeof x.audio !== 'string' || !VOICE_FILE_ID.test(x.audio))) return `${where} รหัสไฟล์เสียงพากย์ไม่ถูกต้อง`;
  if (!num(x.dur) || (x.dur as number) < 0 || (x.dur as number) > VIDEO_MAX_SECONDS || (x.audio === null && x.dur !== 0)) return `${where} ความยาวเสียงพากย์ไม่ถูกต้อง`;
  if (typeof x.made !== 'string' || x.made.length > 2000) return `${where} ข้อมูลเสียงพากย์เสีย`;
  if (!num(x.extra) || (x.extra as number) < 0 || (x.extra as number) > VIDEO_MAX_SECONDS) return `${where} เวลาที่ยืดให้เสียงพากย์ไม่ถูกต้อง`;
  return null;
}

function checkClip(c: unknown, n: number, version: number): string | null {
  const where = `คลิปที่ ${n}`;
  if (!c || typeof c !== 'object') return `${where} เสีย`;
  const x = c as Record<string, unknown>;
  if (typeof x.id !== 'string' || !MEDIA_ID.test(x.id)) return `${where} ไม่มีรหัสที่ถูกต้อง`;
  if (typeof x.media !== 'string' || !(MEDIA_ID.test(x.media) || (version >= 3 && x.media === '' && !!x.slot))) return `${where} ไม่มีรหัสไฟล์ที่ถูกต้อง`;
  if (typeof x.name !== 'string' || x.name.length > 200) return `${where} มีชื่อไฟล์ยาวเกิน 200 ตัวอักษร`;
  for (const k of ['srcDur', 'srcStart', 'srcEnd', 'copyDur', 'in', 'out', 'volume'] as const) {
    if (!num(x[k]) || (x[k] as number) < 0) return `${where} มีค่า ${k} ไม่ถูกต้อง`;
  }
  const v = x as unknown as VideoClip;
  if (v.srcEnd <= v.srcStart || v.srcEnd > v.srcDur + 0.5) return `${where} ช่วงจากไฟล์ต้นฉบับไม่ถูกต้อง`;
  if (v.copyDur <= 0) return `${where} ความยาวไม่ถูกต้อง`;
  if (v.out - v.in < CLIP_MIN_SECONDS - 0.001 || v.out > v.copyDur + 0.05) return `${where} ช่วงที่ใช้ไม่ถูกต้อง`;
  if (v.volume > 1) return `${where} ระดับเสียงต้องอยู่ระหว่าง 0 ถึง 1`;
  if (typeof x.hasAudio !== 'boolean') return `${where} ไม่ได้บอกว่ามีเสียงหรือไม่`;
  if (version >= 2 && typeof x.fade !== 'boolean') return `${where} ไม่ได้บอกว่าจางที่รอยต่อหรือไม่`;
  if (version >= 4) { const why = checkVoice(x.voice, where); if (why) return why; }
  else if (x.voice !== undefined) return `${where} มีเสียงพากย์แต่ข้อมูลเป็นรุ่นเก่า`;
  if (version >= 3) {
    if (x.kind !== 'video' && x.kind !== 'photo') return `${where} ไม่ได้บอกว่าเป็นคลิปหรือรูป`;
    if (!num(x.hold) || (x.hold as number) < 0 || (x.hold as number) > VIDEO_MAX_SECONDS) return `${where} มีเวลาค้างภาพไม่ถูกต้อง`;
    if (x.slot !== null) {
      const sl = x.slot as Record<string, unknown> | undefined;
      if (!sl || typeof sl !== 'object' || typeof sl.label !== 'string' || typeof sl.hint !== 'string') return `${where} ข้อมูลช่องเสีย`;
      const why = checkSlotText(sl.label, sl.hint);
      if (why) return `${where}: ${why}`;
    }
    if (x.kind === 'photo' && (x.hasAudio || v.copyDur > PHOTO_MAX_SECONDS + 0.05)) return `${where} (รูปนิ่ง) ข้อมูลไม่ถูกต้อง`;
  }
  return null;
}

/** Validate a project; returns its length in milliseconds. */
export function checkVideo(raw: unknown): Checked<{ durationMs: number }> {
  if (!raw || typeof raw !== 'object') return { ok: false, error: 'ข้อมูลวิดีโอเสีย' };
  const p = raw as Record<string, unknown>;
  if (p.v !== 1 && p.v !== 2 && p.v !== 3 && p.v !== 4) return { ok: false, error: 'ข้อมูลวิดีโอเป็นรุ่นที่ระบบไม่รู้จัก กรุณารีเฟรชหน้าเว็บ' };
  if ((p.v as number) >= 2 && (typeof p.fadeIn !== 'boolean' || typeof p.fadeOut !== 'boolean')) return { ok: false, error: 'ข้อมูลการจางตอนเริ่ม/จบวิดีโอไม่ถูกต้อง' };
  if (p.v === 4) {
    const n = p.narration as Record<string, unknown> | undefined;
    if (!n || typeof n !== 'object' || typeof n.subs !== 'boolean') return { ok: false, error: 'ข้อมูลเสียงพากย์ของวิดีโอเสีย' };
    if (n.choice !== null) { const why = checkChoice(n.choice); if (why) return { ok: false, error: `เสียงพากย์ของวิดีโอ: ${why}` }; }
  }
  if (!Array.isArray(p.clips)) return { ok: false, error: 'ข้อมูลวิดีโอไม่มีรายการคลิป' };
  if (p.clips.length > VIDEO_MAX_CLIPS) return { ok: false, error: `วิดีโอหนึ่งมีได้สูงสุด ${VIDEO_MAX_CLIPS} คลิป` };
  for (let k = 0; k < p.clips.length; k++) {
    const why = checkClip(p.clips[k], k + 1, p.v as number);
    if (why) return { ok: false, error: why };
  }
  if (p.music !== null) {
    const m = p.music as Record<string, unknown> | undefined;
    if (!m || typeof m !== 'object' || typeof m.media !== 'string' || !MEDIA_ID.test(m.media)
      || typeof m.name !== 'string' || m.name.length > 200 || !num(m.dur) || (m.dur as number) <= 0
      || !num(m.volume) || (m.volume as number) < 0 || (m.volume as number) > 1) {
      return { ok: false, error: 'ข้อมูลเพลงประกอบไม่ถูกต้อง' };
    }
    if (m.library !== undefined && m.library !== null && (typeof m.library !== 'string' || !LIBRARY_ID.test(m.library))) {
      return { ok: false, error: 'รหัสเพลงจากคลังเพลงไม่ถูกต้อง' };
    }
  }
  const total = totalLength(p as unknown as VideoProject);
  if (total > VIDEO_MAX_SECONDS + 0.05) return { ok: false, error: `วิดีโอยาวได้สูงสุด ${VIDEO_MAX_SECONDS / 60} นาที (งานนี้ยาว ${fmtTime(total)})` };
  return { ok: true, value: { durationMs: Math.round(total * 1000) } };
}

/** file names are kept short enough for checkVideo (200); `shorten` keeps Thai marks on their letter (print/rules shortText) */
export const NAME_MAX_CHARS = 90;

/** a short random id for clips and media keys */
export function newId(): string {
  const b = new Uint8Array(12);
  crypto.getRandomValues(b);
  return Array.from(b, x => 'abcdefghijklmnopqrstuvwxyz0123456789'[x % 36]).join('');
}
